import React, { useState, useMemo } from 'react';

/* ========================= Holiday Definitions ========================= */

// Returns holidays for a given year with calculated dates
const getHolidaysForYear = (year) => [
  // Fixed-date holidays
  { name: "New Year's Day", date: `${year}-01-01`, defaultType: 'closed' },
  { name: "Valentine's Day", date: `${year}-02-14`, defaultType: 'closed' },
  { name: "St. Patrick's Day", date: `${year}-03-17`, defaultType: 'closed' },
  { name: "Cinco de Mayo", date: `${year}-05-05`, defaultType: 'closed' },
  { name: "Independence Day", date: `${year}-07-04`, defaultType: 'closed' },
  { name: "Halloween", date: `${year}-10-31`, defaultType: 'closed' },
  { name: "Veterans Day", date: `${year}-11-11`, defaultType: 'closed' },
  { name: "Christmas Eve", date: `${year}-12-24`, defaultType: 'closed' },
  { name: "Christmas Day", date: `${year}-12-25`, defaultType: 'closed' },
  { name: "New Year's Eve", date: `${year}-12-31`, defaultType: 'closed' },
  
  // Calculated holidays (nth weekday of month)
  { name: "MLK Day", date: getNthWeekdayOfMonth(year, 1, 1, 3), defaultType: 'closed' }, // 3rd Monday of January
  { name: "Presidents Day", date: getNthWeekdayOfMonth(year, 2, 1, 3), defaultType: 'closed' }, // 3rd Monday of February
  { name: "Memorial Day", date: getLastWeekdayOfMonth(year, 5, 1), defaultType: 'closed' }, // Last Monday of May
  { name: "Labor Day", date: getNthWeekdayOfMonth(year, 9, 1, 1), defaultType: 'closed' }, // 1st Monday of September
  { name: "Columbus Day", date: getNthWeekdayOfMonth(year, 10, 1, 2), defaultType: 'modified' }, // 2nd Monday of October
  { name: "Thanksgiving", date: getNthWeekdayOfMonth(year, 11, 4, 4), defaultType: 'closed' }, // 4th Thursday of November
  { name: "Black Friday", date: getDayAfter(getNthWeekdayOfMonth(year, 11, 4, 4)), defaultType: 'modified' },
  
  // Easter (complex calculation)
  { name: "Easter Sunday", date: getEasterDate(year), defaultType: 'closed' },
  { name: "Mother's Day", date: getNthWeekdayOfMonth(year, 5, 0, 2), defaultType: 'modified' }, // 2nd Sunday of May
  { name: "Father's Day", date: getNthWeekdayOfMonth(year, 6, 0, 3), defaultType: 'modified' }, // 3rd Sunday of June
];

// Get the nth occurrence of a weekday in a month (weekday: 0=Sun, 1=Mon, etc.)
function getNthWeekdayOfMonth(year, month, weekday, n) {
  const firstDay = new Date(year, month - 1, 1);
  let dayOffset = weekday - firstDay.getDay();
  if (dayOffset < 0) dayOffset += 7;
  const day = 1 + dayOffset + (n - 1) * 7;
  return formatDateISO(new Date(year, month - 1, day));
}

// Get last occurrence of a weekday in a month
function getLastWeekdayOfMonth(year, month, weekday) {
  const lastDay = new Date(year, month, 0); // Last day of month
  let dayOffset = lastDay.getDay() - weekday;
  if (dayOffset < 0) dayOffset += 7;
  return formatDateISO(new Date(year, month - 1, lastDay.getDate() - dayOffset));
}

// Get day after a date string
function getDayAfter(dateStr) {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + 1);
  return formatDateISO(date);
}

// Easter calculation (Anonymous Gregorian algorithm)
function getEasterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return formatDateISO(new Date(year, month - 1, day));
}

function formatDateISO(date) {
  return date.toISOString().split('T')[0];
}

// Map JS day (0=Sun) to schedule day names
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Get day of week name from date string
function getDayOfWeek(dateStr) {
  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day);
  return dayNames[date.getDay()];
}

// Extract hours from Firebase snapshot for a specific day
function getHoursForDay(hoursSnapshot, service, dayName) {
  if (!hoursSnapshot) return null;
  
  const serviceSnap = hoursSnapshot.child(service);
  if (!serviceSnap.val()) return null;
  
  const daySnap = serviceSnap.child(dayName);
  if (!daySnap.val()) return null;
  
  // Convert Firebase format to our array format
  const ranges = [];
  daySnap.forEach((rangeSnap) => {
    const from = rangeSnap.child('from').val();
    const to = rangeSnap.child('to').val();
    if (from && to) {
      ranges.push({
        from: { hour: Number(from.hour), minute: Number(from.minute || 0) },
        to: { hour: Number(to.hour), minute: Number(to.minute || 0) },
      });
    }
  });
  
  return ranges.length > 0 ? ranges : null;
}

// Get all service hours for a specific date
function getDefaultHoursForDate(hoursSnapshot, dateStr) {
  const dayName = getDayOfWeek(dateStr);
  
  return {
    dineIn: getHoursForDay(hoursSnapshot, 'dineIn', dayName) || 
            [{ from: { hour: 11, minute: 0 }, to: { hour: 21, minute: 0 } }],
    pickUp: getHoursForDay(hoursSnapshot, 'pickUp', dayName) || 
            [{ from: { hour: 11, minute: 0 }, to: { hour: 21, minute: 0 } }],
    delivery: getHoursForDay(hoursSnapshot, 'delivery', dayName) || 
              [{ from: { hour: 11, minute: 0 }, to: { hour: 20, minute: 30 } }],
  };
}

/* ========================= Helpers ========================= */

const militaryHourToUS = (hour) => {
  hour = Number(hour);
  if (hour === 0) return "12am";
  if (hour === 12) return "12pm";
  if (hour > 12) return `${hour - 12}pm`;
  return `${hour}am`;
};

const formatTime = (timeObj) => {
  if (!timeObj) return '--';
  const hour = timeObj.hour ?? timeObj.child?.('hour')?.val?.();
  const min = timeObj.minute ?? timeObj.child?.('minute')?.val?.();
  if (hour === undefined || hour === null) return '--';
  return `${militaryHourToUS(hour)}${min ? `:${String(min).padStart(2, '0')}` : ''}`;
};

const formatDateForDisplay = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

/* ========================= Time Editor ========================= */

function TimeEditor({ value, onChange }) {
  const hour = value?.hour ?? '';
  const minute = value?.minute ?? 0;

  const updateHour = (e) => onChange({ ...value, hour: Number(e.target.value), minute });
  const updateMinute = (e) => onChange({ ...value, hour, minute: Number(e.target.value) });

  return (
    <div className="cse-time-editor">
      <select value={hour} onChange={updateHour}>
        <option value="">--</option>
        {[...Array(24).keys()].map(h => (
          <option key={h} value={h}>{militaryHourToUS(h)}</option>
        ))}
      </select>
      <span className="cse-time-separator">:</span>
      <select value={minute} onChange={updateMinute}>
        {[0, 15, 30, 45].map(m => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
    </div>
  );
}

/* ========================= Time Range Editor ========================= */

function TimeRangeEditor({ value, onChange, onDelete }) {
  const from = value?.from || { hour: 11, minute: 0 };
  const to = value?.to || { hour: 21, minute: 0 };

  return (
    <div className="cse-time-range">
      <TimeEditor 
        value={from} 
        onChange={(newFrom) => onChange({ from: newFrom, to })} 
      />
      <span className="cse-arrow">→</span>
      <TimeEditor 
        value={to} 
        onChange={(newTo) => onChange({ from, to: newTo })} 
      />
      <button className="cse-btn-delete-range" onClick={onDelete} title="Remove time range">×</button>
    </div>
  );
}

/* ========================= Service Hours Editor ========================= */

function ServiceHoursEditor({ label, ranges, onChange }) {
  const addRange = () => {
    const newRanges = [...(ranges || []), { from: { hour: 11, minute: 0 }, to: { hour: 21, minute: 0 } }];
    onChange(newRanges);
  };

  const updateRange = (index, newValue) => {
    const newRanges = [...ranges];
    newRanges[index] = newValue;
    onChange(newRanges);
  };

  const deleteRange = (index) => {
    const newRanges = ranges.filter((_, i) => i !== index);
    onChange(newRanges.length > 0 ? newRanges : null);
  };

  return (
    <div className="cse-service-hours">
      <div className="cse-service-label">{label}</div>
      <div className="cse-service-ranges">
        {(!ranges || ranges.length === 0) && (
          <span className="cse-closed-tag">Closed</span>
        )}
        {ranges?.map((range, i) => (
          <TimeRangeEditor
            key={i}
            value={range}
            onChange={(val) => updateRange(i, val)}
            onDelete={() => deleteRange(i)}
          />
        ))}
        <button className="cse-btn-add-range" onClick={addRange}>+ Add Hours</button>
      </div>
    </div>
  );
}

/* ========================= Unified Hours Editor ========================= */

function UnifiedHoursEditor({ hours, onChange }) {
  // Use dineIn as the source of truth for unified editing
  const unifiedRanges = hours.dineIn || [{ from: { hour: 11, minute: 0 }, to: { hour: 21, minute: 0 } }];

  const handleUnifiedChange = (newRanges) => {
    onChange({
      dineIn: newRanges,
      pickUp: newRanges,
      delivery: newRanges,
    });
  };

  const addRange = () => {
    const newRanges = [...unifiedRanges, { from: { hour: 11, minute: 0 }, to: { hour: 21, minute: 0 } }];
    handleUnifiedChange(newRanges);
  };

  const updateRange = (index, newValue) => {
    const newRanges = [...unifiedRanges];
    newRanges[index] = newValue;
    handleUnifiedChange(newRanges);
  };

  const deleteRange = (index) => {
    const newRanges = unifiedRanges.filter((_, i) => i !== index);
    handleUnifiedChange(newRanges.length > 0 ? newRanges : null);
  };

  const renderTimeRanges = (ranges) => {
    if (!ranges || ranges.length === 0) return <span className="cse-closed-inline">Closed</span>;
    return ranges.map((r, i) => (
      <span key={i} className="cse-time-display">
        {formatTime(r.from)} → {formatTime(r.to)}
        {i < ranges.length - 1 && ', '}
      </span>
    ));
  };

  return (
    <div className="cse-unified-hours">
      <div className="cse-service-hours">
        <div className="cse-service-label">All Services</div>
        <div className="cse-service-ranges">
          {(!unifiedRanges || unifiedRanges.length === 0) && (
            <span className="cse-closed-tag">Closed</span>
          )}
          {unifiedRanges?.map((range, i) => (
            <TimeRangeEditor
              key={i}
              value={range}
              onChange={(val) => updateRange(i, val)}
              onDelete={() => deleteRange(i)}
            />
          ))}
          <button className="cse-btn-add-range" onClick={addRange}>+ Add Hours</button>
        </div>
      </div>
      <div className="cse-unified-preview">
        <div className="cse-unified-preview-label">Hours will be set for:</div>
        <div className="cse-hours-row">
          <span className="cse-label">Dine In:</span>
          {renderTimeRanges(hours.dineIn)}
        </div>
        <div className="cse-hours-row">
          <span className="cse-label">Pick Up:</span>
          {renderTimeRanges(hours.pickUp)}
        </div>
        <div className="cse-hours-row">
          <span className="cse-label">Delivery:</span>
          {renderTimeRanges(hours.delivery)}
        </div>
      </div>
    </div>
  );
}

/* ========================= Upcoming Holidays Quick Pick ========================= */

function UpcomingHolidays({ existingDates, onSelect }) {
  const upcomingHolidays = useMemo(() => {
    const today = new Date();
    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
    
    const currentYear = today.getFullYear();
    const nextYear = currentYear + 1;
    
    // Get holidays for current and next year to handle year boundaries
    const allHolidays = [
      ...getHolidaysForYear(currentYear),
      ...getHolidaysForYear(nextYear),
    ];
    
    return allHolidays
      .filter(h => {
        const holidayDate = new Date(h.date);
        return holidayDate >= today && holidayDate <= oneMonthFromNow;
      })
      .filter(h => !existingDates.includes(h.date))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [existingDates]);

  if (upcomingHolidays.length === 0) return null;

  return (
    <div className="cse-quick-picks">
      <div className="cse-quick-picks-label">Quick Add Upcoming Holidays:</div>
      <div className="cse-quick-picks-list">
        {upcomingHolidays.map(holiday => (
          <button
            key={holiday.date}
            className={`cse-quick-pick-btn ${holiday.defaultType}`}
            onClick={() => onSelect(holiday)}
            title={`Add ${holiday.name} (${formatDateForDisplay(holiday.date)})`}
          >
            <span className="cse-qp-name">{holiday.name}</span>
            <span className="cse-qp-date">{formatShortDate(holiday.date)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function formatShortDate(dateStr) {
  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ========================= Add New Entry Form ========================= */

function AddEntryForm({ onAdd, onCancel, initialData, hoursSnapshot }) {
  const today = new Date().toISOString().split('T')[0];
  
  // Get default hours based on selected date's day of week
  const getDefaultHours = (dateStr) => {
    if (!dateStr) {
      return {
        dineIn: [{ from: { hour: 11, minute: 0 }, to: { hour: 21, minute: 0 } }],
        pickUp: [{ from: { hour: 11, minute: 0 }, to: { hour: 21, minute: 0 } }],
        delivery: [{ from: { hour: 11, minute: 0 }, to: { hour: 20, minute: 30 } }],
      };
    }
    return getDefaultHoursForDate(hoursSnapshot, dateStr);
  };

  const [date, setDate] = useState(initialData?.date || '');
  const [type, setType] = useState(initialData?.type || 'closed');
  const [reason, setReason] = useState(initialData?.reason || '');
  const [hours, setHours] = useState(() => getDefaultHours(initialData?.date));
  const [editIndividually, setEditIndividually] = useState(false);
  
  // Update hours when date changes
  const handleDateChange = (newDate) => {
    setDate(newDate);
    if (newDate) {
      setHours(getDefaultHours(newDate));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!date) return;

    const entry = { type, reason, enabled: false }; // Default to disabled
    if (type === 'modified') {
      entry.hours = hours;
    }
    onAdd(date, entry);
  };

  return (
    <form className="cse-add-form" onSubmit={handleSubmit}>
      <div className="cse-form-header">
        <h3>Add Date Override</h3>
        <button type="button" className="cse-btn-close" onClick={onCancel}>×</button>
      </div>

      <div className="cse-form-row">
        <label>Date</label>
        <input 
          type="date" 
          value={date} 
          onChange={(e) => handleDateChange(e.target.value)}
          min={today}
          required
        />
        {date && <span className="cse-day-hint">{getDayOfWeek(date)} hours will be used as default</span>}
      </div>

      <div className="cse-form-row">
        <label>Type</label>
        <div className="cse-type-toggle">
          <button 
            type="button"
            className={`cse-type-btn ${type === 'closed' ? 'active closed' : ''}`}
            onClick={() => setType('closed')}
          >
            Closed
          </button>
          <button 
            type="button"
            className={`cse-type-btn ${type === 'modified' ? 'active modified' : ''}`}
            onClick={() => setType('modified')}
          >
            Modified Hours
          </button>
        </div>
      </div>

      <div className="cse-form-row">
        <label>Reason</label>
        <input 
          type="text" 
          value={reason} 
          onChange={(e) => setReason(e.target.value)}
          placeholder="optional, will display on website banner"
        />
      </div>

      {type === 'modified' && (
        <div className="cse-hours-section">
          <label className="cse-checkbox-label">
            <input 
              type="checkbox" 
              checked={editIndividually} 
              onChange={(e) => setEditIndividually(e.target.checked)}
            />
            Edit individually
          </label>
          
          {editIndividually ? (
            <>
              <ServiceHoursEditor 
                label="Dine In" 
                ranges={hours.dineIn} 
                onChange={(val) => setHours({ ...hours, dineIn: val })}
              />
              <ServiceHoursEditor 
                label="Pick Up" 
                ranges={hours.pickUp} 
                onChange={(val) => setHours({ ...hours, pickUp: val })}
              />
              <ServiceHoursEditor 
                label="Delivery" 
                ranges={hours.delivery} 
                onChange={(val) => setHours({ ...hours, delivery: val })}
              />
            </>
          ) : (
            <UnifiedHoursEditor 
              hours={hours}
              onChange={setHours}
            />
          )}
        </div>
      )}

      <div className="cse-form-actions">
        <button type="button" className="cse-btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="submit" className="cse-btn-save" disabled={!date}>Save Override</button>
      </div>
    </form>
  );
}

/* ========================= Schedule Entry Card ========================= */

function ScheduleEntryCard({ dateKey, snapshot, onDelete, hoursSnapshot }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editIndividually, setEditIndividually] = useState(false);
  const data = snapshot.val() || {};
  const type = data.type || 'closed';
  const reason = data.reason || '';
  const hours = data.hours || {};
  const enabled = data.enabled === true; // Explicitly check for true

  const updateField = (field, value) => {
    snapshot.ref.child(field).set(value);
  };

  const toggleEnabled = () => {
    snapshot.ref.child('enabled').set(!enabled);
  };

  const updateHours = (service, ranges) => {
    if (ranges === null || ranges.length === 0) {
      snapshot.ref.child(`hours/${service}`).remove();
    } else {
      snapshot.ref.child(`hours/${service}`).set(ranges);
    }
  };

  const updateAllHours = (newHours) => {
    snapshot.ref.child('hours').set(newHours);
  };

  const handleTypeChange = (newType) => {
    updateField('type', newType);
    if (newType === 'closed') {
      snapshot.ref.child('hours').remove();
    } else if (newType === 'modified' && !data.hours) {
      // Use regular schedule hours for this day as default
      const defaultHours = getDefaultHoursForDate(hoursSnapshot, dateKey);
      snapshot.ref.child('hours').set(defaultHours);
    }
  };

  const renderTimeRanges = (ranges) => {
    if (!ranges || ranges.length === 0) return <span className="cse-closed-inline">Closed</span>;
    return ranges.map((r, i) => (
      <span key={i} className="cse-time-display">
        {formatTime(r.from)} → {formatTime(r.to)}
        {i < ranges.length - 1 && ', '}
      </span>
    ));
  };

  return (
    <div className={`cse-card ${type}${enabled ? '' : ' disabled'}`}>
      <div className="cse-card-header">
        <div className="cse-date-badge">
          <button 
            className={`cse-toggle-btn ${enabled ? 'enabled' : 'disabled'}`}
            onClick={toggleEnabled}
            title={enabled ? 'Click to disable' : 'Click to enable'}
          >
            {enabled ? 'ON' : 'OFF'}
          </button>
          <span className="cse-date">{formatDateForDisplay(dateKey)}</span>
          <span className={`cse-type-badge ${type}`}>
            {type === 'closed' ? 'CLOSED' : 'MODIFIED'}
          </span>
        </div>
        <div className="cse-card-actions">
          <button 
            className="cse-btn-edit" 
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Done' : 'Edit'}
          </button>
          <button className="cse-btn-delete" onClick={onDelete}>Delete</button>
        </div>
      </div>

      {reason && <div className="cse-reason">"{reason}"</div>}

      {isEditing ? (
        <div className="cse-edit-section">
          <div className="cse-form-row">
            <label>Type</label>
            <div className="cse-type-toggle">
              <button 
                type="button"
                className={`cse-type-btn ${type === 'closed' ? 'active closed' : ''}`}
                onClick={() => handleTypeChange('closed')}
              >
                Closed
              </button>
              <button 
                type="button"
                className={`cse-type-btn ${type === 'modified' ? 'active modified' : ''}`}
                onClick={() => handleTypeChange('modified')}
              >
                Modified Hours
              </button>
            </div>
          </div>

          <div className="cse-form-row">
            <label>Reason</label>
            <input 
              type="text" 
              value={reason} 
              onChange={(e) => updateField('reason', e.target.value)}
              placeholder="optional, will display on website banner"
            />
          </div>

          {type === 'modified' && (
            <div className="cse-hours-section">
              <label className="cse-checkbox-label">
                <input 
                  type="checkbox" 
                  checked={editIndividually} 
                  onChange={(e) => setEditIndividually(e.target.checked)}
                />
                Edit individually
              </label>
              
              {editIndividually ? (
                <>
                  <ServiceHoursEditor 
                    label="Dine In" 
                    ranges={hours.dineIn} 
                    onChange={(val) => updateHours('dineIn', val)}
                  />
                  <ServiceHoursEditor 
                    label="Pick Up" 
                    ranges={hours.pickUp} 
                    onChange={(val) => updateHours('pickUp', val)}
                  />
                  <ServiceHoursEditor 
                    label="Delivery" 
                    ranges={hours.delivery} 
                    onChange={(val) => updateHours('delivery', val)}
                  />
                </>
              ) : (
                <UnifiedHoursEditor 
                  hours={hours}
                  onChange={updateAllHours}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        type === 'modified' && (
          <div className="cse-hours-summary">
            <div className="cse-hours-row">
              <span className="cse-label">Dine In:</span>
              {renderTimeRanges(hours.dineIn)}
            </div>
            <div className="cse-hours-row">
              <span className="cse-label">Pick Up:</span>
              {renderTimeRanges(hours.pickUp)}
            </div>
            <div className="cse-hours-row">
              <span className="cse-label">Delivery:</span>
              {renderTimeRanges(hours.delivery)}
            </div>
          </div>
        )
      )}
    </div>
  );
}

/* ========================= MAIN COMPONENT ========================= */

function CustomScheduleEditor({ snapshot, hoursSnapshot }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formInitialData, setFormInitialData] = useState(null);

  // Convert snapshot to sorted array of entries
  const entries = [];
  const existingDates = [];
  if (snapshot?.val()) {
    snapshot.forEach((child) => {
      entries.push({ key: child.key, snapshot: child });
      existingDates.push(child.key);
    });
  }
  // Sort by date descending (upcoming first)
  entries.sort((a, b) => b.key.localeCompare(a.key));

  const handleAdd = (date, entry) => {
    snapshot.ref.child(date).set(entry);
    setShowAddForm(false);
    setFormInitialData(null);
  };

  const handleDelete = (dateKey) => {
    if (window.confirm(`Delete override for ${formatDateForDisplay(dateKey)}?`)) {
      snapshot.ref.child(dateKey).remove();
    }
  };

  const handleHolidayQuickPick = (holiday) => {
    // Pre-fill the form with holiday data instead of creating entry directly
    setFormInitialData({
      date: holiday.date,
      type: holiday.defaultType,
      reason: '',
    });
    setShowAddForm(true);
  };

  return (
    <div className="custom-schedule-editor">
      <UpcomingHolidays 
        existingDates={existingDates} 
        onSelect={handleHolidayQuickPick}
      />

      {!showAddForm && (
        <button className="cse-btn-add" onClick={() => { setFormInitialData(null); setShowAddForm(true); }}>
          + Add Custom Date
        </button>
      )}

      {showAddForm && (
        <AddEntryForm 
          key={formInitialData?.date || 'new'}
          onAdd={handleAdd} 
          onCancel={() => { setShowAddForm(false); setFormInitialData(null); }}
          initialData={formInitialData}
          hoursSnapshot={hoursSnapshot}
        />
      )}

      <div className="cse-entries-list">
        {entries.length === 0 && !showAddForm && (
          <div className="cse-empty-state">
            <p>No custom schedules configured.</p>
            <p className="cse-hint">Add date overrides for holidays, special events, or temporary closures.</p>
          </div>
        )}
        {entries.map(({ key, snapshot: childSnapshot }) => (
          <ScheduleEntryCard 
            key={key}
            dateKey={key}
            snapshot={childSnapshot}
            onDelete={() => handleDelete(key)}
            hoursSnapshot={hoursSnapshot}
          />
        ))}
      </div>
    </div>
  );
}

export default CustomScheduleEditor;

